/**
 * "Por que a venda foi perdida?" — ao levar uma oportunidade para uma etapa de
 * perda (058).
 *
 * ── Pergunta ANTES de mover ──────────────────────────────────────────────
 *
 * O card só sai do lugar depois da resposta. Mover primeiro e perguntar depois
 * deixaria o card na perda sem motivo quando a pessoa fechasse a janela, que é
 * o estado que a lista de motivos existe para impedir. O servidor recusa do
 * mesmo jeito; a janela é para ninguém chegar à recusa.
 *
 * ── Sem motivo cadastrado ────────────────────────────────────────────────
 *
 * A revenda que ainda não escreveu a lista não fica com o funil travado: a
 * janela diz que não há motivos, e a perda segue só com a observação.
 */

import { useId, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { Botao, CampoArea, CampoSelecao, Dialogo } from '@/shared/ui';

export interface PerdaEscolhida {
  readonly motivo_id: string | null;
  readonly observacao?: string;
}

export function DialogoDePerda({
  titulo,
  aoConfirmar,
  aoFechar,
}: {
  /** O nome da oportunidade, para a pessoa confirmar que é a certa. */
  titulo: string;
  aoConfirmar: (perda: PerdaEscolhida) => void;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const motivos = useQuery({
    queryKey: chaves.motivosDePerda(),
    enabled: api !== null,
    queryFn: ({ signal }) => api!.motivosDePerda({ sinal: signal }),
  });
  const ativos = (motivos.data?.itens ?? []).filter((m) => m.ativo);
  const semLista = motivos.isSuccess && ativos.length === 0;

  const [motivo, definirMotivo] = useState('');
  const [observacao, definirObservacao] = useState('');
  const descricao = useId();

  const pronto = semLista || motivo !== '';

  return (
    <Dialogo
      titulo="Por que a venda foi perdida?"
      largura="estreita"
      aoFechar={aoFechar}
      descricaoId={descricao}
      botoes={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            desabilitado={!pronto}
            motivo="Escolha o motivo antes de confirmar."
            onClick={() =>
              aoConfirmar({
                motivo_id: motivo === '' ? null : motivo,
                ...(observacao.trim() ? { observacao: observacao.trim() } : {}),
              })
            }
          >
            Marcar como perdida
          </Botao>
        </>
      }
    >
      <p id={descricao}>
        {titulo} vai para uma etapa de perda.{' '}
        {semLista
          ? 'Esta organização ainda não tem motivos de perda cadastrados: escreva o que ' +
            'aconteceu na observação.'
          : 'O motivo é o que permite ver, depois, por que as vendas estão sendo perdidas.'}
      </p>

      {motivos.isPending && <p>Buscando os motivos…</p>}
      {!semLista && motivos.isSuccess && (
        <CampoSelecao
          rotulo="Motivo"
          value={motivo}
          onChange={(e) => definirMotivo(e.currentTarget.value)}
        >
          <option value="">Escolha um motivo</option>
          {ativos.map((m) => (
            <option key={m.id} value={m.id}>
              {m.texto}
            </option>
          ))}
        </CampoSelecao>
      )}

      <CampoArea
        rotulo="Observação (opcional)"
        value={observacao}
        maxLength={500}
        rows={3}
        onChange={(e) => definirObservacao(e.currentTarget.value)}
      />
    </Dialogo>
  );
}
